import { Info } from 'lucide-react';
import { useMemo } from 'react';

import type {
  DemographicStat,
  ExtendedProfile,
  Location,
  Profile,
} from '../../../entities/post/models';
import { PostFilter } from '../../../entities/post/models';
import { showDrillDownContextMenu } from '../conversation/overlay/show-drill-down-context-menu';
import { useFilteredPostsNavigation } from '../filtered-posts/hook/use-filtered-posts-navigation';
import { useFilteredProfilesNavigation } from '../filtered-profiles/hook/use-filtered-profiles-navigation';
import { useAnchor } from '../shared/hooks/use-anchor';
import { AgeTable } from './components/age-table';
import { GenderTable } from './components/gender-table';
import { LocationTable } from './components/location-table';
import { ProfileTable } from './components/profile-table';
import { RaceTable } from './components/race-table';
import { PEOPLE_PAGE_ANCHOR } from './constants';
import { usePeopleInsight } from './hooks/use-people-insight';

import { PUBLIC_FILTER_TYPE } from '@/entities/public-filter/constants';
import { SectionHeader } from '@/shared/components/section-header';
import { TooltipFlat } from '@/shared/components/shadcn/tooltip/tooltip-flat';
import { useSelectedSavedQuery } from '@/shared/hooks/use-selected-saved-query';
import { useSharedDateRange } from '@/shared/hooks/use-shared-date-range';
import { formatNumber } from '@/shared/utils/format-number';
import { useRestoreScroll } from '../insight-layout/hook/use-insight-scroll-container';
import { getProfileEngagementRate } from '../shared/helpers/get-profile-engagement-rate';
import { usePublicFilter } from '../shared/public-filter/use-public-filter';

export function PeoplePage() {
  return (
    <div className="pb-12">
      <PeoplePageContent />
    </div>
  );
}

function PeoplePageContent() {
  const { query } = useSelectedSavedQuery();
  const { dateRange } = useSharedDateRange();
  const { filterValues, setFilterValues, publicFilter } = usePublicFilter(query.id);
  const { data: peopleInsight, isLoading } = usePeopleInsight({
    queryId: query.id,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    publicFilter,
  });
  const navigateToFilteredPosts = useFilteredPostsNavigation();
  const navigateToFilteredProfiles = useFilteredProfilesNavigation();

  const handleProfileClick = async (profile: Profile, event: React.MouseEvent) => {
    const action = await showDrillDownContextMenu({
      position: { x: event.clientX, y: event.clientY },
      menus: ['view-posts'],
    });

    switch (action) {
      case 'view-posts': {
        // Get current dimension from URL
        const currentParams = new URLSearchParams(window.location.search);
        const currentDimension = currentParams.get('dimension') as PostFilter;

        await navigateToFilteredPosts({
          filter: {
            [PostFilter.Profile]: profile.username,
          },
          // Only set dimension to Profile if there's no existing dimension
          dimension: currentDimension ? currentDimension : PostFilter.Profile,
        });
        break;
      }
      case 'close':
        break;
    }
  };

  const handleGenderClick = async (gender: DemographicStat, event: React.MouseEvent) => {
    const action = await showDrillDownContextMenu({
      position: { x: event.clientX, y: event.clientY },
    });

    switch (action) {
      case 'view-posts': {
        await navigateToFilteredPosts();
        const newFilterValues = [
          ...filterValues,
          { filterType: PUBLIC_FILTER_TYPE.GENDER, value: gender.name },
        ];
        setFilterValues(newFilterValues);
        break;
      }

      case 'view-profiles': {
        await navigateToFilteredProfiles();
        const newFilterValues = [
          ...filterValues,
          { filterType: PUBLIC_FILTER_TYPE.GENDER, value: gender.name },
        ];
        setFilterValues(newFilterValues);
        break;
      }

      case 'close':
        break;
    }
  };

  const handleRaceClick = async (race: DemographicStat, event: React.MouseEvent) => {
    const action = await showDrillDownContextMenu({
      position: { x: event.clientX, y: event.clientY },
    });

    switch (action) {
      case 'view-posts': {
        await navigateToFilteredPosts();
        const newFilterValues = [
          ...filterValues,
          { filterType: PUBLIC_FILTER_TYPE.RACE, value: race.name },
        ];
        setFilterValues(newFilterValues);
        break;
      }

      case 'view-profiles': {
        await navigateToFilteredProfiles();
        const newFilterValues = [
          ...filterValues,
          { filterType: PUBLIC_FILTER_TYPE.RACE, value: race.name },
        ];
        setFilterValues(newFilterValues);
        break;
      }
      case 'close':
        break;
    }
  };

  const handleAgeClick = async (age: DemographicStat, event: React.MouseEvent) => {
    const action = await showDrillDownContextMenu({
      position: { x: event.clientX, y: event.clientY },
    });

    switch (action) {
      case 'view-posts': {
        await navigateToFilteredPosts();
        const newFilterValues = [
          ...filterValues,
          { filterType: PUBLIC_FILTER_TYPE.AGE, value: age.name },
        ];
        setFilterValues(newFilterValues);
        break;
      }
      case 'view-profiles': {
        await navigateToFilteredProfiles();
        const newFilterValues = [
          ...filterValues,
          { filterType: PUBLIC_FILTER_TYPE.AGE, value: age.name },
        ];
        setFilterValues(newFilterValues);
        break;
      }
      case 'close':
        break;
    }
  };

  const handleLocationClick = async (location: Location, event: React.MouseEvent) => {
    const action = await showDrillDownContextMenu({
      position: { x: event.clientX, y: event.clientY },
    });

    switch (action) {
      case 'view-posts': {
        await navigateToFilteredPosts();
        const newFilterValues = [
          ...filterValues,
          { filterType: PUBLIC_FILTER_TYPE.LOCATION, value: location.code },
        ];
        setFilterValues(newFilterValues);
        break;
      }
      case 'view-profiles': {
        await navigateToFilteredProfiles();
        const newFilterValues = [
          ...filterValues,
          { filterType: PUBLIC_FILTER_TYPE.LOCATION, value: location.code },
        ];
        setFilterValues(newFilterValues);
        break;
      }
      case 'close':
        break;
    }
  };

  const extendedProfiles: ExtendedProfile[] = useMemo(() => {
    return peopleInsight?.profiles?.map(getProfileEngagementRate) ?? [];
  }, [peopleInsight?.profiles]);

  useAnchor({ currentPath: '/workspace/$workspaceId/insight/people' });
  useRestoreScroll();

  return (
    <div>
      <div>
        <SectionHeader
          title={`Profiles (${formatNumber(peopleInsight?.totalProfileCount ?? 0)})`}
          description="Explore key engagement metrics to identify your top influencers"
        />
        <div className="p-6" data-anchor={PEOPLE_PAGE_ANCHOR.PEOPLE_TABLE}>
          <ProfileTable
            profiles={extendedProfiles}
            onProfileClick={handleProfileClick}
            isLoading={isLoading}
          />
        </div>
      </div>
      <div>
        <SectionHeader
          title={
            <div className="flex items-center gap-1">
              Demographic
              <TooltipFlat
                contentClassName="max-w-[306px] text-sm"
                content="Gender, race, and age are AI-predicted based on the main individual(s) in the video. Results may not reflect actual information."
              >
                <Info className="size-5" />
              </TooltipFlat>
            </div>
          }
          description="Explore key engagement metrics to identify your top influencers"
        />
        <div className="p-6" data-anchor={PEOPLE_PAGE_ANCHOR.GENDER_TABLE}>
          <GenderTable
            genderData={peopleInsight?.gender ?? []}
            onGenderClick={handleGenderClick}
            isLoading={isLoading}
          />
        </div>
        <div className="p-6" data-anchor={PEOPLE_PAGE_ANCHOR.RACE_TABLE}>
          <RaceTable
            raceData={peopleInsight?.race ?? []}
            onRaceClick={handleRaceClick}
            isLoading={isLoading}
          />
        </div>
        <div className="p-6" data-anchor={PEOPLE_PAGE_ANCHOR.AGE_TABLE}>
          <AgeTable
            ageData={peopleInsight?.age ?? []}
            onAgeClick={handleAgeClick}
            isLoading={isLoading}
          />
        </div>
      </div>
      <div>
        <SectionHeader
          title={'Location'}
          description="Where conversations are happening in the world"
        />
        <div className="p-6" data-anchor={PEOPLE_PAGE_ANCHOR.LOCATION_TABLE}>
          <LocationTable
            locationData={peopleInsight?.locations ?? []}
            onLocationClick={handleLocationClick}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
}
